import {api, RequestParam} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {showPdfPreview} from "@/features/pdfPreview/showPdfPreview";
import {confirmExportOfEmptyTable} from "./confirmExportOfEmptyTable";
import {ValidationRecordEventDetail, ValidationsCollapseComponent} from "@/features/validations/validationsCollapseComponent";
import {TreeDropdownURLs} from "@/shared/ui/treeDropdown";
import {TimeNavigationBar} from "@/shared/ui/timeNavigationBar";
import {RecordingTable, RecordingDataTableResponse} from "@/shared/recording/recordingTable";
import {showRecordingBulkResult} from "@/shared/bulkOperationHelper";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {BulkSelectorContextMenu} from "@/shared/ui/bulkSelectorContextMenu";
import {DataTableBulkSelector} from "@/shared/ui/dataTableBulkSelector";
import * as Constants from "@/shared/constants";
import {Combobox} from "@/shared/ui/combobox";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {getISODate} from "@/shared/utils/datefunctions";
import {
	CustomFieldDefinition,
	CustomFieldOption,
	ReportRecord,
	User
} from "@/shared/timr";
import {
	CustomFieldsForRecordType,
	DateTimeChangeableByUserDayRangeMode,
	ExportType,
	RecordedEntryStatus,
	ReportViewType,
	TimeNavigationType
} from "@/shared/timrTypes";
import {shouldAddStatusNumber} from "@/shared/securityHelper";
import {ReportingQueryParams, serializeQueryParamValue} from "@/shared/queryParams";

export type ReportExportUrl =
	| "export/driveLogReport.json"
	| "export/driveLogRevisionsReport.json"
	| "export/projectTimeReport.json"
	| "export/workingTimeReport.json";

export type ReportBulkDeleteUrl =
	| "/reports/bulkDeleteDriveLogs.json"
	| "/reports/bulkDeleteProjectTimes.json"
	| "/reports/bulkDeleteWorkingTimes.json";

export type ReportBulkSetStatusUrl =
	| "/reports/updateDriveLogStatus.json"
	| "/reports/updateProjectTimeStatus.json"
	| "/reports/updateWorkingTimeStatus.json";

export interface RecordingReportBaseConfig {
	formSelector: string;
	roundingFiltersSelector: string;
	filterButtonSelector: string;
	bulkOperationDropDownSelector: string;
	bulkOperationButtonSelector: string;
	exportDropDownSelector: string;
	exportButtonSelector: string;
	roundingModeSelector: string;
	roundToMinutesSelector: string;
	groupDropdownSelector: string;
	userIdSelector: string;
	userFilterSelector: string;
	userCaretSelector: string;
	showInactiveSelector: string;
	bulkOperationDeleteConfirmMessage: string;
	bulkOperationDeleteUrl: ReportBulkDeleteUrl;
	bulkOperationSetStatusUrl: ReportBulkSetStatusUrl;
	exportBaseUrl: ReportExportUrl;
	exportStatusBaseUrl: string;
	accountExpiredTitle: string;
	accountExpired: string;
	exportRevisionBaseUrl?: ReportExportUrl;
	dropdownUrls?: TreeDropdownURLs;
}

export interface RecordingReportConfig {
	statusNumbers: RecordedEntryStatus[];
	defaultStatusNumber: number;
	customFieldDefinitions: CustomFieldDefinition[];
	users: User[];
	allowedUserIds: number[];
	defaultUserId: number;
	dateTimeChangeableByUserDayRangeUntil: string | null;
	dateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	isGroupLeaderForThemself: boolean;
}

export abstract class RecordingReport<T extends ReportRecord> {
	public static readonly EXCLUDE_ELEMENTS_DBLCLICK_EVENT = ".select-checkbox";

	protected timeNavigationBar: TimeNavigationBar;
	protected recordingTable: RecordingTable<T>;
	protected groupDropdown: GroupDropdown;
	protected bulkOperationButtonContextMenu: BulkSelectorContextMenu;
	protected currentUserId: number;
	protected users: User[];
	protected userCombobox: Combobox<User>;
	protected tableDataLoaded: boolean = false;
	protected validationsCollapseComponent: ValidationsCollapseComponent;

	constructor(public config: RecordingReportConfig, public baseConfig: RecordingReportBaseConfig) {
		this.init();
		this.users = config.users;

		// Hydrate from URL after full init (incl. subclass init()) is done.
		this.applyQueryParams();
	}

	protected init() {
		this.timeNavigationBar = new TimeNavigationBar({
			container: $(".breadcrumb-line-dashboard"),
			timeSpanChanged: (from, to, type) => {
				BrowserStorageSettings.setReportingDateFrom(Constants.GROUP_TIME_NAV_BAR, from);
				BrowserStorageSettings.setReportingDateTo(Constants.GROUP_TIME_NAV_BAR, to);
				BrowserStorageSettings.setReportingDateType(Constants.GROUP_TIME_NAV_BAR, type);
				this.filterTable();
			},
			defaultDateType: BrowserStorageSettings.getReportingDateType(Constants.GROUP_TIME_NAV_BAR),
			defaultFromDate: BrowserStorageSettings.getReportingDateFrom(Constants.GROUP_TIME_NAV_BAR),
			defaultToDate: BrowserStorageSettings.getReportingDateTo(Constants.GROUP_TIME_NAV_BAR)
		});
		this.userCombobox = new Combobox({
			items: this.config.users,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $(this.baseConfig.userIdSelector),
			filterElement: $(this.baseConfig.userFilterSelector),
			caretElement: $(this.baseConfig.userCaretSelector)
		});
		if (globals.isAdmin || globals.isGroupLeader) {
			this.currentUserId = BrowserStorageSettings.getReportUserFilter(Constants.GROUP_REPORT_FILTER);
			this.userCombobox.setSelectedItemById(this.currentUserId);
		}
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, (e, user: User) => {
			this.currentUserId = user ? user.id : null;
			BrowserStorageSettings.setReportUserFilter(Constants.GROUP_REPORT_FILTER, this.currentUserId);
			this.filterTable();
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $(this.baseConfig.groupDropdownSelector),
			allowSelectionOfNotAssigned: false
		}, this.baseConfig.dropdownUrls);
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group) => {
			this.filterTable();
			this.reloadUserList();
		});
		this.initCustomFieldComboboxes();
		$(this.baseConfig.formSelector).find(":input:not(.tree-dropdown-query, .typeahead-dropdown-query)").change(() => {
			this.filterTable();
		});
		this.recordingTable = this.createRecordingTable();
		this.recordingTable.getDataTable().on("init.dt", () => {
			$(this.baseConfig.exportButtonSelector).show();
			$(".reportExportButtonLoadingIndicator").hide();
		});
		this.recordingTable.getDataTable().on("processing.dt", (e, settings, processing) => {
			if (processing) {
				this.tableDataLoaded = false;
				Button.setLoading($(this.baseConfig.bulkOperationButtonSelector));
				Button.setLoading($(this.baseConfig.exportButtonSelector));
			} else {
				this.tableDataLoaded = true;
				Button.resetLoading($(this.baseConfig.bulkOperationButtonSelector));
				Button.resetLoading($(this.baseConfig.exportButtonSelector));
				/*
				 * Button.resetLoading(...) resets the text/content of the button to the initial value (independent of the last state)
				 * So it is necessary to update it here again (with the actually needed text - if there are selected elem. or not)
				 * without setTimeout this doesn't work (probably the Button.resetLoading(...) interfere with the jQuery code to toggle it)
				 */
				setTimeout(() => {
					this.updateExportButtonText();
				}, 0);
			}
		});
		$(this.baseConfig.filterButtonSelector).click(e => {
			e.preventDefault();
			this.filterTable();
		});
		$(this.baseConfig.showInactiveSelector).change(() => {
			this.onShowInactiveChanged($(this.baseConfig.showInactiveSelector).is(":checked"));
		});
		this.handleBulkOperationContextMenu();
		$(this.baseConfig.exportDropDownSelector).find("a").click(e => {
			const link = $(e.currentTarget);
			confirmExportOfEmptyTable(this.recordingTable.getRowCount(), () => link.data("preview")
				? this.preview()
				: this.export(link.data("exportType") as ExportType, !!link.data("exportRevisions")));
		});
		$(this.recordingTable.bulkSelector).on(DataTableBulkSelector.EVENT_BULK_SELECTION_CHANGED, (e, recordings: T[]) => {
			this.onBulkSelectionChanged(recordings);
		});
		$(this.recordingTable.bulkSelector).on(DataTableBulkSelector.EVENT_BULK_SELECTION_TOGGLED, (e, recording: T, selected: boolean) => {
			this.onBulkSelectionToggled(recording, selected);
		});
		$(this.baseConfig.roundingModeSelector).change(() => {
			this.filterTable();
		});
		$(this.baseConfig.roundToMinutesSelector).change(() => {
			this.filterTable();
		});
		$(this.recordingTable).on(RecordingTable.EVENT_SHOW_RECORDING, (e, recording: T) => {
			this.show(recording);
		});
		$(this.recordingTable).on(RecordingTable.EVENT_EDIT_RECORDING, (e, recording: T) => {
			this.edit(recording);
		});
		$(this.recordingTable).on(RecordingTable.EVENT_DUPLICATE_RECORDING, (e, recording: T) => {
			this.duplicate(recording);
		});

		$(".reload-table").click(() => {
			this.reloadTable();
		});
	}

	protected handleBulkOperationContextMenu() {
		this.bulkOperationButtonContextMenu = this.createBulkMenu();
		const dropdownMenu = $(this.baseConfig.bulkOperationButtonSelector).closest(".dropdown").find(".dropdown-menu");
		if (dropdownMenu.length !== 0) {
			dropdownMenu.remove();
		}
		$(this.baseConfig.bulkOperationButtonSelector).after(this.bulkOperationButtonContextMenu.render());
		this.bulkOperationButtonContextMenu.registerOnButton($(this.baseConfig.bulkOperationButtonSelector));
	}

	private initCustomFieldComboboxes() {
		for (const definition of this.config.customFieldDefinitions) {
			if (definition.fieldType === CustomFieldsForRecordType.Options && definition.fieldName) {
				const customFieldComboboxItems: CustomFieldOption[] = [];
				for (const item of definition.optionsList) {
					customFieldComboboxItems.push({id: item});
				}
				const customFieldCombobox = new Combobox({
					items: customFieldComboboxItems,
					formatter: (item) => {
						return item.id;
					},
					idElement: $(this.baseConfig.formSelector).find(`[id="customField${definition.fieldNumber}"]`),
					filterElement: $(this.baseConfig.formSelector).find(`[id="customField${definition.fieldNumber}Filter"]`),
					caretElement: $(this.baseConfig.formSelector).find(`[id="customField${definition.fieldNumber}Caret"]`),
				});
				$(customFieldCombobox).on(Combobox.EVENT_CHANGED, () => {
					this.filterTable();
				});
			}
		}
	}

	protected abstract createRecordingTable(): RecordingTable<T>;

	protected abstract createAddRecordDialog(preSelectUser: User): void;

	protected abstract getSecurityStatus(): boolean;

	protected abstract getRecordById(recordId: number, link: JQuery): void;

	protected initValidationsListeners() {
		if (globals.savedValidationsEnabled) {
			$(this.recordingTable).on(RecordingTable.EVENT_DATA_CHANGED, (e, data: RecordingDataTableResponse<T>) => {
				const relevantValidations = data.validations?.relevant ?? [];
				this.recordingTable.updateSeverityHeaderIcon(relevantValidations);

				// update data of validations collapse
				this.validationsCollapseComponent.content = {
					validations: relevantValidations,
					// DEVNOTE isAddWorkingTimeButtonActive/isAddAbsenceButtonActive are only relevant for validations which contain an "add wt" context menu
					// These are currently only displayed in the time account/validations page, so they are set to false here
					isAddWorkingTimeButtonActive: false,
					isAddAbsenceButtonActive: false,
					validationTotalCount: relevantValidations.length
				};
			});

			// init validations web component and add type
			this.validationsCollapseComponent = document.getElementById("validationsCollapse") as ValidationsCollapseComponent;

			// handle click events on referenced records of validations
			this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_CLICKED, e => {
				const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
				this.getRecordById(customEvent.detail.id, customEvent.detail.link);
			});
			this.validationsCollapseComponent.addEventListener(ValidationsCollapseComponent.EVENT_RECORD_REF_CLICKED, e => {
				const customEvent = e as CustomEvent<ValidationRecordEventDetail>;
				this.getRecordById(customEvent.detail.id, customEvent.detail.link);
			});

			this.recordingTable.getDataTable().on("processing.dt", (e, s, processing: boolean) => {
				// set collapse loading on table reload
				this.validationsCollapseComponent.toggleLoadingOverlay(processing);
			});
		}
	}

	protected reloadTable(): JQueryPromise<unknown> {
		return this.recordingTable.reload();
	}

	protected filterTable(): JQueryPromise<unknown> {
		return this.recordingTable.filter(this.buildFilterParams());
	}

	protected onShowInactiveChanged(showInactive: boolean) {
		this.reloadUserList();
	}

	protected buildFilterParams(): RequestParam[] {
		return [
			...$(this.baseConfig.formSelector).serializeArray(),
			{name: "startDate", value: getISODate(this.timeNavigationBar.getDateFrom())},
			{name: "endDate", value: getISODate(this.timeNavigationBar.getDateTo())},
			...$(this.baseConfig.roundingFiltersSelector).serializeArray(),
		];
	}

	protected buildBulkOperationParams(): RequestParam[] {
		return [
			...this.buildFilterParams(),
			{name: "allSelected", value: this.recordingTable.bulkSelector.isAllSelected()},
			{name: "idsInclude", value: this.recordingTable.bulkSelector.getIdsToInclude()?.join(",")},
			{name: "idsExclude", value: this.recordingTable.bulkSelector.getIdsToExclude()?.join(",")},
		];
	}

	protected buildUserListParams(): RequestParam[] {
		return [
			{name: "activeUsersOnly", value: !$(this.baseConfig.showInactiveSelector).is(":checked")},
			{name: "groupId", value: this.groupDropdown.getSelectedId()},
		];
	}

	protected reloadUserList() {
		api("reports/filterUsers.json").get(this.buildUserListParams()).done(data => {
			if (data) {
				if (data.users) {
					this.setUsers(data.users);
				}
				this.recordingTable.setTaskLeader(data.isTaskLeader === true);
			}
		});
	}

	protected setUsers(users: User[]) {
		this.users = users;
		this.userCombobox.updateItems(users);
	}

	protected bulkOperationDelete() {
		const count = this.recordingTable.bulkSelector.getSelectedEntriesCount();
		const confirmMessage = messages.withArguments(this.baseConfig.bulkOperationDeleteConfirmMessage, count);
		BootstrapModal.confirm({
			message: confirmMessage,
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
				Button.setLoading(button);
				api(this.baseConfig.bulkOperationDeleteUrl)
					.post(this.buildBulkOperationParams())
					.done(result => {
						if (!result.allUpdated) {
							showRecordingBulkResult(result, "record.bulkdelete.");
						}
						this.filterTable();
					})
					.always(() => {
						Button.resetLoading(button);
						modal.close();
					});
			}
		});
	}

	protected bulkOperationSetStatus(statusNumber: number) {
		const count = this.recordingTable.bulkSelector.getSelectedEntriesCount();
		const statusName = messages["reports.filter.status." + statusNumber];
		const confirmMessage = messages.withArguments("report.setStatus.confirm", count, statusName);
		BootstrapModal.confirm({
			message: confirmMessage,
			yesLabel: "report.setStatus.changeStatus",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
				Button.setLoading(button);
				const params = this.buildBulkOperationParams();
				params.push({name: "statusNumber", value: statusNumber});
				api(this.baseConfig.bulkOperationSetStatusUrl)
					.post(params)
					.done(result => {
						if (!result.allUpdated) {
							showRecordingBulkResult(result, "report.setStatus.");
						}
						this.reloadTable();
					})
					.always(() => {
						Button.resetLoading(button);
						modal.close();
					});
			}
		});
	}

	private export(exportType: ExportType, withRevisions: boolean) {
		void this.requestExport(exportType, withRevisions).then(exportJobId => new DownloadStatus({
			exportJobId,
			exportStatusBaseUrl: this.baseConfig.exportStatusBaseUrl,
			exportType: exportType,
			loadingMessage: messages[`downloadStatus.loadingMessage.reports.${exportType}`],
			allowCancellation: true
		}));
	}

	private preview() {
		void this.requestExport(ExportType.Pdf, false).then(exportJobId => showPdfPreview(
			messages["reports.export.preview"], exportJobId, messages[`downloadStatus.loadingMessage.reports.${ExportType.Pdf}`]));
	}

	/**
	 * Starts the export job and resolves with its id; never resolves if no job was started.
	 * Derived classes may ask for additional parameters before the export starts.
	 */
	protected requestExport(exportType: ExportType, withRevisions: boolean, additionalParams?: RequestParam[]): Promise<string> {
		let baseUrl: ReportExportUrl;

		if (withRevisions && this.baseConfig.exportRevisionBaseUrl) {
			baseUrl = this.baseConfig.exportRevisionBaseUrl;
		} else {
			baseUrl = this.baseConfig.exportBaseUrl;
		}

		let params = this.buildFilterParams();
		params.push({name: "exportFileType", value: exportType});
		params.push({name: "allSelected", value: this.recordingTable.bulkSelector.isAllSelected()});
		params.push({name: "idsInclude", value: this.recordingTable.bulkSelector.getIdsToInclude()?.join(",")});
		params.push({name: "idsExclude", value: this.recordingTable.bulkSelector.getIdsToExclude()?.join(",")});

		if (additionalParams) {
			params = params.concat(additionalParams);
		}

		return new Promise(resolve => {
			void api(baseUrl)
				.post(params)
				.done(data => {
					if (data.exportJobId) {
						resolve(data.exportJobId);
					}
				});
		});
	}

	protected show(recording: T) {
		// maybe overridden in derived classes
	}

	protected edit(recording: T) {
		// maybe overridden in derived classes
	}

	protected duplicate(recording: T) {
		// maybe overridden in derived classes
	}

	protected onBulkSelectionChanged(recordings: T[]) {
		$(this.baseConfig.bulkOperationButtonSelector).toggle(this.recordingTable.bulkSelector.hasSelectedEntries());
		this.updateExportButtonText();
	}

	protected onBulkSelectionToggled(recording: T, selected: boolean) {
		// maybe overridden in derived classes
	}

	protected formatPercent(value: number): string {
		return Number(value * 100).toFixed(0) + "%";
	}

	protected createBulkMenu() {
		const menu = new BulkSelectorContextMenu();

		if (this.config.statusNumbers.length !== 0) {
			this.config.statusNumbers.forEach(statusNumber => {
				const statusName = messages["reports.filter.status." + statusNumber];
				const title = messages.withArguments("report.setStatus", statusName);
				menu.addMenuEntry({
					label: title,
					active: true,
					action: () => this.bulkOperationSetStatus(statusNumber),
					// if for some records are not updated due missing permission, there will be a information shown after the operation
					// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
					show: () => shouldAddStatusNumber(statusNumber, null, globals.isTaskLeader),
				});
			});
			menu.addDivider();
		}

		return menu;
	}

	protected showAddRecordDialog() {
		if (!this.getSecurityStatus()) {
			BootstrapModal.info({
				title: this.baseConfig.accountExpiredTitle,
				message: this.baseConfig.accountExpired,
				escapeText: false
			});
			return;
		}
		const userIdInput = $(this.baseConfig.userIdSelector);
		let preSelectUser: User = null;
		if (userIdInput.length === 1) {
			preSelectUser = this.userCombobox.getSelectedItem();
		}
		this.createAddRecordDialog(preSelectUser);
	}

	private updateExportButtonText() {
		$(this.baseConfig.exportButtonSelector).find("span").toggle(this.recordingTable.bulkSelector.hasSelectedEntries());
	}

	// ----------------------
	// Query params
	// ----------------------


	/**
	 * Record-type-specific pages override this so URL iteration only sees keys valid for that report
	 * (see `WorkingTimeReportingQueryParams`, etc.).
	 */
	protected abstract getReportingQueryParams(): typeof ReportingQueryParams;


	/**
	 * Optional per-page hook for typed query params that need custom handling.
	 * Return handled keys so base mapping skips them and push async hydration requests into `pendingHydrationRequests`.
	 */
	protected applyRecordTypeQueryParams(recordTypeQp: typeof ReportingQueryParams, pendingHydrationRequests: PromiseLike<unknown>[]): string[] {
		return this.applyBaseQueryParams(recordTypeQp, pendingHydrationRequests);
	}

	private applyQueryParams() {
		const recordTypeQp = this.getReportingQueryParams();
		const form = $(this.baseConfig.formSelector);
		const pendingHydrationRequests: PromiseLike<unknown>[] = [];
		const handledRecordTypeKeys = this.applyRecordTypeQueryParams(recordTypeQp, pendingHydrationRequests);
		const appliedRecordTypeQueryParams = handledRecordTypeKeys.length > 0;
		const appliedFormQueryParams = this.applyMappedQueryParams(recordTypeQp, handledRecordTypeKeys, form);

		if (appliedRecordTypeQueryParams || appliedFormQueryParams) {
			Promise.allSettled(pendingHydrationRequests).finally(() => this.filterTable());
		}
	}

	private applyBaseQueryParams(
		recordTypeQp: typeof ReportingQueryParams,
		pendingHydrationRequests: PromiseLike<unknown>[]
	): string[] {
		const applied: string[] = [];

		applied.push(...this.applyTimeSpanQueryParams(recordTypeQp));
		applied.push(...this.applyUserQueryParam(recordTypeQp));
		applied.push(...this.applyGroupQueryParam(recordTypeQp, pendingHydrationRequests));

		return applied;
	}

	private applyTimeSpanQueryParams(recordTypeQp: typeof ReportingQueryParams): string[] {
		const viewType = recordTypeQp.get("viewType");
		const dateFrom = recordTypeQp.get("dateFrom") ?? recordTypeQp.get("date");
		const dateTo = recordTypeQp.get("dateTo");
		const timeNavigationType = recordTypeQp.get("timeNavigationType")
			?? (viewType === ReportViewType.WEEKLY ? TimeNavigationType.Week : viewType === ReportViewType.DAILY ? TimeNavigationType.Day : undefined);

		if (!timeNavigationType || !dateFrom && !dateTo) {
			return [];
		}

		this.timeNavigationBar.setTimeSpan(dateFrom ?? dateTo, dateTo ?? dateFrom, timeNavigationType);
		return ["viewType", "dateFrom", "dateTo", "timeNavigationType", "date"];
	}

	private applyUserQueryParam(recordTypeQp: typeof ReportingQueryParams): string[] {
		const userId = recordTypeQp.get("userId");
		if (!userId || !this.userCombobox) {
			return [];
		}

		this.userCombobox.setSelectedItemById(userId, false, false);
		return ["userId"];
	}

	private applyGroupQueryParam(
		recordTypeQp: typeof ReportingQueryParams,
		pendingHydrationRequests: PromiseLike<unknown>[]
	): string[] {
		const groupId = recordTypeQp.get("groupId");
		if (!groupId || !this.groupDropdown) {
			return [];
		}

		const request = api("reports/getGroup.json")
			.get({groupId})
			.done(group => {
				if (group?.id) {
					this.groupDropdown.setSelection(group.id, group.name, group.breadCrumbs, group);
				}
			});

		pendingHydrationRequests.push(request);
		return ["groupId"];
	}

	private applyMappedQueryParams(
		recordTypeQp: typeof ReportingQueryParams,
		excludeKeys: string[],
		form: JQuery
	): boolean {
		let applied = false;

		for (const key of recordTypeQp.keys()) {
			if (excludeKeys.includes(String(key))) {
				continue;
			}

			const value = recordTypeQp.get(key);
			if (value === undefined) {
				continue;
			}

			const elements = form.find(`[name="${String(key)}"]`);
			if (elements.length === 0) {
				continue;
			}

			elements.each((_, el) => {
				$(el).val(serializeQueryParamValue(value) ?? "");
				applied = true;
			});
		}

		return applied;
	}
}
